import type { Device, IDeviceWRelsById } from "types/device.types"
import type { Powerline } from "types/powerlines.types"
import type { PlotDataSource } from "types/dashboard.types"
import type { Entity } from "types/common.types"

import { getTowerDevicesIds, getTowerPowerline } from "./towers"
import { getPowerlineAndTowerDevicesIds, getPowerlineDevicesIds } from "./powerlines"

export const resolverNeedsDevices = (resolver?: string): boolean => {
  if (!resolver) return false
  return ["tower.devices", "powerline.allDevices", "powerline.directDevices"].includes(
    resolver,
  )
}

export const resolverNeedsPowerlines = (resolver?: string): boolean => {
  if (!resolver) return false
  return ["tower.powerline"].includes(resolver)
}

type IDResolver = (context: {
  entityId: number
  entity?: Entity
  devices?: IDeviceWRelsById
  powerlinesById?: Record<number, Powerline>
  powerlineIdByTowerId?: Record<number, number>
}) => number[]

export const ID_RESOLVERS: Record<string, IDResolver> = {
  // Device -> get device tower
  "device.tower": ({ entityId, devices = {} }) => {
    const deviceWRels = devices[entityId]
    return deviceWRels?.device.settings?.tower_id
      ? [deviceWRels.device.settings?.tower_id]
      : []
  },
  // Device -> get device power line
  "device.powerline": ({ entityId, devices = {} }) => {
    const deviceWRels = devices[entityId]
    return deviceWRels?.device.settings?.powerline_id
      ? [deviceWRels.device.settings?.powerline_id]
      : []
  },
  // Device -> get device span
  "device.span": ({ entity }) => {
    const spanId = (entity as Device).settings?.span_id
    return spanId ? [spanId] : []
  },
  // Tower -> get all devices on that tower
  "tower.devices": ({ entityId, devices = {} }) => {
    return getTowerDevicesIds(devices, entityId)
  },
  // Tower -> get the power line for this tower
  "tower.powerline": ({ entityId, powerlinesById = {}, powerlineIdByTowerId = {} }) => {
    const powerline = getTowerPowerline(entityId, powerlinesById, powerlineIdByTowerId)
    return powerline ? [powerline.id] : []
  },
  // Power line -> get all towers on this powerline
  "powerline.towers": ({ entity }) => {
    return (entity as Powerline)?.tower_ids ?? []
  },
  // Powerline -> get all spans on this powerline
  "powerline.spans": ({ entity }) => {
    return (entity as Powerline)?.span_ids ?? []
  },
  // Powerline -> get devices on the powerline AND on its towers
  "powerline.allDevices": ({ entityId, entity, devices = {} }) => {
    const powerline = entity as Powerline
    const towerIds = new Set(powerline.tower_ids || [])
    return getPowerlineAndTowerDevicesIds(devices, entityId, towerIds)
  },
  // Powerline -> get only devices directly on powerline
  "powerline.directDevices": ({ entityId, devices = {} }) => {
    return getPowerlineDevicesIds(devices, entityId)
  },
}

export const resolveEntityIds = (
  dataSource: PlotDataSource,
  contextEntityId: number,
  contextEntity?: Entity,
  devices?: IDeviceWRelsById,
  powerlinesById?: Record<number, Powerline>,
  powerlineIdByTowerId?: Record<number, number>,
): number[] => {
  if (dataSource.resolver === "self") {
    return [contextEntityId]
  }

  const resolver = ID_RESOLVERS[dataSource.resolver]
  if (resolver) {
    return resolver({
      entityId: contextEntityId,
      entity: contextEntity,
      devices,
      powerlinesById,
      powerlineIdByTowerId,
    })
  }

  return []
}
